import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Plus, FileCheck, Search, Edit, Trash2,
  CheckCircle, XCircle, AlertCircle, Eye, FileText,
  ClipboardCheck, Flame, Shield, Wrench, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QCSheets = () => {
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSheetModal, setShowSheetModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingSheet, setEditingSheet] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load templates
      const templatesRes = await axios.get(`${API}/qc-sheets/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(templatesRes.data.templates || []);
      
      // Load existing sheets
      const sheetsRes = await axios.get(`${API}/qc-sheets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSheets(sheetsRes.data.sheets || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load QC sheets');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplateModal(false);
    setShowSheetModal(true);
  };

  const handleCreateSheet = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/qc-sheets`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('QC sheet created successfully');
      setShowSheetModal(false);
      setSelectedTemplate(null);
      loadData();
    } catch (error) {
      console.error('Failed to create sheet:', error);
      toast.error('Failed to create QC sheet');
    }
  };

  const handleDeleteSheet = async (sheetId) => {
    if (!window.confirm('Are you sure you want to delete this QC sheet?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/qc-sheets/${sheetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('QC sheet deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete sheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete QC sheet');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      procedure: <FileText size={20} />,
      qualification: <Shield size={20} />,
      inspection: <ClipboardCheck size={20} />,
      testing: <Flame size={20} />,
      control: <Package size={20} />,
      master: <CheckCircle size={20} />
    };
    return icons[category] || <FileCheck size={20} />;
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'secondary',
      in_progress: 'default',
      under_review: 'default',
      approved: 'default',
      rejected: 'destructive'
    };
    return variants[status] || 'secondary';
  };

  const filteredSheets = sheets.filter(sheet => {
    const matchesSearch = sheet.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sheet.sheet_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sheet.status === statusFilter;
    
    // Get category from templates
    const template = templates.find(t => t.sheet_type === sheet.sheet_type);
    const sheetCategory = template?.category || 'general';
    const matchesCategory = categoryFilter === 'all' || sheetCategory === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Group templates by category
  const templatesByCategory = templates.reduce((acc, template) => {
    const category = template.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button
              onClick={() => navigate('/tools')}
              variant="outline"
              size="sm"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Tools
            </Button>
          </div>
          <h1 className="text-4xl font-black tracking-tight uppercase text-gray-900">QC Sheets</h1>
          <p className="text-gray-700 mt-1 font-medium">Quality Control Documentation & Inspection Management</p>
        </div>
        <Button onClick={() => setShowTemplateModal(true)}>
          <Plus size={16} className="mr-2" />
          New QC Sheet
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-3 text-gray-500" />
                <Input
                  placeholder="Search QC sheets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-gray-900 border-gray-300"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="procedure">Procedure</SelectItem>
                <SelectItem value="qualification">Qualification</SelectItem>
                <SelectItem value="inspection">Inspection</SelectItem>
                <SelectItem value="testing">Testing</SelectItem>
                <SelectItem value="control">Control</SelectItem>
                <SelectItem value="master">Master</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* QC Sheets List */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-700">
            Loading QC sheets...
          </CardContent>
        </Card>
      ) : filteredSheets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileCheck size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-900 mb-2 font-semibold text-lg">No QC sheets found</p>
            <p className="text-sm text-gray-700 mb-4">
              Create quality control sheets to document your welding procedures and inspections
            </p>
            <Button onClick={() => setShowTemplateModal(true)}>
              <Plus size={16} className="mr-2" />
              Create Your First QC Sheet
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSheets.map((sheet) => {
            const template = templates.find(t => t.sheet_type === sheet.sheet_type);
            return (
              <Card key={sheet.id} className="hover:border-primary transition-all">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {template && getCategoryIcon(template.category)}
                        <Badge variant={getStatusBadge(sheet.status)} className="text-xs">
                          {sheet.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-xs font-mono text-gray-600 mb-1 font-semibold">
                        {sheet.sheet_number} Rev. {sheet.revision}
                      </p>
                      <CardTitle className="text-base text-gray-900">{sheet.title}</CardTitle>
                      <p className="text-xs text-gray-700 mt-1">
                        {template?.name || sheet.sheet_type}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">Created:</span>
                      <span className="font-mono text-xs text-gray-900">
                        {new Date(sheet.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {sheet.approved_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-700 font-medium">Approved:</span>
                        <span className="font-mono text-xs text-gray-900">
                          {new Date(sheet.approved_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/qc-sheets/${sheet.id}`)}
                      >
                        <Eye size={14} className="mr-2" />
                        View
                      </Button>
                      {sheet.status !== 'approved' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteSheet(sheet.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Stats Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900">QC Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-gray-50">
              <p className="text-3xl font-bold text-gray-900">{sheets.length}</p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">Total Sheets</p>
            </div>
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-gray-50">
              <p className="text-3xl font-bold text-gray-600">
                {sheets.filter(s => s.status === 'draft').length}
              </p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">Draft</p>
            </div>
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-yellow-50">
              <p className="text-3xl font-bold text-yellow-600">
                {sheets.filter(s => s.status === 'in_progress').length}
              </p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">In Progress</p>
            </div>
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-orange-50">
              <p className="text-3xl font-bold text-orange-600">
                {sheets.filter(s => s.status === 'under_review').length}
              </p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">Under Review</p>
            </div>
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-green-50">
              <p className="text-3xl font-bold text-green-600">
                {sheets.filter(s => s.status === 'approved').length}
              </p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">Approved</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Selection Modal */}
      <TemplateSelectionModal
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={templatesByCategory}
        onSelectTemplate={handleSelectTemplate}
        getCategoryIcon={getCategoryIcon}
      />

      {/* Sheet Creation Modal */}
      <SheetCreationModal
        open={showSheetModal}
        onClose={() => {
          setShowSheetModal(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        onSubmit={handleCreateSheet}
      />
    </div>
  );
};

// Template Selection Modal Component
const TemplateSelectionModal = ({ open, onClose, templates, onSelectTemplate, getCategoryIcon }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gray-900 text-xl">Select QC Sheet Template</DialogTitle>
          <DialogDescription className="text-gray-700">
            Choose a template to create a new quality control sheet
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="procedure">Procedure</TabsTrigger>
            <TabsTrigger value="qualification">Qualification</TabsTrigger>
            <TabsTrigger value="inspection">Inspection</TabsTrigger>
            <TabsTrigger value="testing">Testing</TabsTrigger>
            <TabsTrigger value="control">Control</TabsTrigger>
            <TabsTrigger value="master">Master</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="space-y-4 mt-4">
            {Object.entries(templates).map(([category, categoryTemplates]) => (
              <div key={category}>
                <h3 className="text-sm font-bold mb-2 capitalize text-gray-900 bg-gray-100 p-2 rounded">{category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categoryTemplates.map((template) => (
                    <Card
                      key={template.sheet_type}
                      className="hover:border-primary transition-all border-gray-300"
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(template.category)}
                            <CardTitle className="text-sm text-gray-900">{template.name}</CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xs text-gray-700 mb-3">
                          {template.description}
                        </p>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="flex-1"
                            onClick={() => onSelectTemplate(template)}
                          >
                            Use Template
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = `/qc-sheets/template-builder/${template.sheet_type}`;
                            }}
                          >
                            Configure
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>
          
          {Object.entries(templates).map(([category, categoryTemplates]) => (
            <TabsContent key={category} value={category} className="space-y-3 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {categoryTemplates.map((template) => (
                  <Card
                    key={template.sheet_type}
                    className="cursor-pointer hover:border-primary transition-all"
                    onClick={() => onSelectTemplate(template)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(template.category)}
                        <CardTitle className="text-sm">{template.name}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground">
                        {template.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Sheet Creation Modal Component (simplified for now)
const SheetCreationModal = ({ open, onClose, template, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      sheet_type: template.sheet_type,
      title: formData.title,
      notes: formData.notes,
      data: {}
    });
    setFormData({ title: '', notes: '' }); // Reset after submit
  };

  if (!template) return null;
  
  // Update title when template changes
  const currentTitle = formData.title || template.name;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-gray-900 text-xl">Create {template.name}</DialogTitle>
          <DialogDescription className="text-gray-700">
            Fill in the basic information to create this QC sheet
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-semibold text-gray-900">Sheet Title *</Label>
            <Input
              id="title"
              value={currentTitle}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter sheet title..."
              required
              className="text-gray-900 border-gray-300"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-semibold text-gray-900">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any additional notes..."
              rows={3}
              className="text-gray-900 border-gray-300"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <p className="text-xs text-gray-800">
              <strong className="text-gray-900">Note:</strong> This is a basic sheet creation. After creation, you&apos;ll be able to fill in detailed information specific to this QC sheet type.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Create Sheet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QCSheets;
